import { SeamHttp, SeamHttpRequest } from "@seamapi/http"
import {
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult,
  useSuspenseQuery,
  UseQueryOptions,
  UseQueryResult,
  useQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  useInfiniteQuery,
  useMutation,
  UseMutationOptions,
  DefaultError,
  UseMutationResult,
  useQueryClient,
  useQueries,
} from "@tanstack/react-query"
import { getSeamHttpClient } from "./use-seam-client"
import { assert } from "src/assert"
import { useCurrentWorkspaceId } from "core/hooks/use-current-workspace-id"
import { TypedAxios } from "typed-axios-instance"
import { getAuthenticatedAxios } from "./use-authenticated-axios"
import {
  RouteResponse,
  RouteRequestBody,
  Routes,
} from "@seamapi/legacy-react-query/all-routes"
import { useEffect } from "react"

type ResponseFromSeamHttpRequest<T> = T extends SeamHttpRequest<
  infer TResponse,
  infer TResponseKey
>
  ? TResponseKey extends keyof TResponse
    ? TResponse[TResponseKey]
    : undefined
  : never

type UseSeamQueryOptions<TRequest extends SeamHttpRequest<any, any>> = Omit<
  UseQueryOptions<ResponseFromSeamHttpRequest<TRequest>>,
  "queryFn" | "queryKey"
> & {
  queryKey?: UseQueryOptions<ResponseFromSeamHttpRequest<TRequest>>["queryKey"]
}

type UseSeamQueryResult<TRequest extends SeamHttpRequest<any, any>> =
  UseQueryResult<ResponseFromSeamHttpRequest<TRequest>, Error> & {
    request: TRequest
  }

/**
 * Use a Seam query to fetch data from the server.
 * Note: This uses  and must be used within a  boundary.
 * @example
 * ```tsx
 * const { data: webhooks } = useSeamQuery((client) => client.webhooks.list())
 * console.log(webhooks)
 * ```
 * @param getRequest Get the `SeamHttpRequest` object to execute the query
 * @param options The options for the query.
 * @returns
 */
export function useSeamQuery<const TRequest extends SeamHttpRequest<any, any>>(
  getRequest: (client: SeamHttp) => TRequest,
  options?: UseSeamQueryOptions<TRequest>
): UseSeamQueryResult<TRequest> {
  const workspace_id = useCurrentWorkspaceId()
  assert(workspace_id, "workspace_id is required")

  const client = getSeamHttpClient(workspace_id)
  const request = getRequest(client)
  const queryKey =
    options?.queryKey ?? deriveQueryKeyFromRequest(workspace_id, request)
  const queryFn = () => {
    return request.execute()
  }
  return {
    ...useQuery({ ...options, queryKey, queryFn }),
    request,
  }
}

type UseSeamSuspenseQueryOptions<TRequest extends SeamHttpRequest<any, any>> =
  Omit<
    UseSuspenseQueryOptions<ResponseFromSeamHttpRequest<TRequest>>,
    "queryFn" | "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      ResponseFromSeamHttpRequest<TRequest>
    >["queryKey"]
  }

type UseSeamSuspenseQueryResult<TRequest extends SeamHttpRequest<any, any>> =
  UseSuspenseQueryResult<ResponseFromSeamHttpRequest<TRequest>, Error> & {
    request: TRequest
  }

/**
 * Use a Seam query to fetch data from the server.
 * Note: This uses Suspense and must be used within a Suspense boundary.
 * @example
 * ```tsx
 * const { data: webhooks } = useSeamSuspenseQuery((client) => client.webhooks.list())
 * console.log(webhooks)
 * ```
 * @param getRequest Get the `SeamHttpRequest` object to execute the query
 * @param options The options for the query.
 * @returns
 */
export function useSeamSuspenseQuery<
  const TRequest extends SeamHttpRequest<any, any>
>(
  getRequest: (client: SeamHttp) => TRequest,
  options?: UseSeamSuspenseQueryOptions<TRequest>
): UseSeamSuspenseQueryResult<TRequest> {
  const workspace_id = useCurrentWorkspaceId()
  assert(workspace_id, "workspace_id is required")

  const client = getSeamHttpClient(workspace_id)
  const request = getRequest(client)
  const queryKey =
    options?.queryKey ?? deriveQueryKeyFromRequest(workspace_id, request)
  const queryFn = () => {
    return request.execute()
  }
  return {
    ...useSuspenseQuery({ ...options, queryKey, queryFn }),
    request,
  }
}

type UseInternalQueryOptions<TResponse> = Omit<
  UseSuspenseQueryOptions<TResponse>,
  "queryFn"
>

type UseInternalQueryResult<TResponse> = UseSuspenseQueryResult<
  TResponse,
  Error
>

/**
 * Use an internal query to fetch data from the server.
 * Note: This uses Suspense and must be used within a Suspense boundary.
 * @example
 * ```tsx
 * const { data: eventTypes } = useInternalQuery(
 *  (axios) =>
 *   axios
 *    .get("/internal/webhooks/get_event_types")
 *    .then((r) => r.data.svix_event_types),
 *  {
 *    queryKey: ["webhookEventTypes"],
 *  }
 * )
 * console.log(eventTypes)
 * ```
 * @param getRequest Get the axios request to execute the query
 * @param options The options for the query.
 * @returns
 */
export function useInternalQuery<const TResponse>(
  getRequest: (axios: TypedAxios<Routes>) => Promise<TResponse>,
  options: UseInternalQueryOptions<TResponse>
): UseInternalQueryResult<TResponse> {
  const workspace_id = useCurrentWorkspaceId()
  assert(workspace_id, "workspace_id is required")

  const queryFn = () => {
    return getRequest(getAuthenticatedAxios(workspace_id))
  }
  return useSuspenseQuery({ ...options, queryFn })
}

const removeHTTPMethodFromPath = (path: string) => {
  return path.replace(/(GET|POST|PUT|DELETE|PATCH)/i, "")
}

export function deriveQueryKeyFromRequest<
  R extends {
    url: {
      pathname: string
    }
    body: unknown
    params?: unknown
  }
>(workspace_id: string, request: R) {
  const { pathname } = request.url
  // GET routes carry their arguments in `params` and leave `body` undefined, so
  // keying on `body` alone collapses every call to one route onto a single
  // cache entry. That is load-bearing here: `/devices/get` and `/acs/users/get`
  // are GET routes, so without `params` two different device_ids would share a
  // cache entry and react-query would serve one device's data for another.
  const { body, params } = request
  return [
    { workspace_id },
    ...removeHTTPMethodFromPath(pathname).split("/").filter(Boolean),
    body ?? params,
  ]
}

type UseSeamInfinityQueryOptions<TRequest extends SeamHttpRequest<any, any>> =
  Omit<
    UseInfiniteQueryOptions<ResponseFromSeamHttpRequest<TRequest>>,
    "queryFn" | "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      ResponseFromSeamHttpRequest<TRequest>
    >["queryKey"]
  }

type UseSeamInfinityQueryResult<TRequest extends SeamHttpRequest<any, any>> =
  UseInfiniteQueryResult<ResponseFromSeamHttpRequest<TRequest>>

/**
 * Use a seam query to fetch data from the server.
 * Note: This uses Suspense and must be used within a Suspense boundary.
 * @example
 * ```tsx
 * const { data: eventTypes } = useSeamInfinityQuery(
 *  (seam) =>
 *   seam.events.list(),
 *  {
 *    queryKey: ["webhookEventTypes"],
 *  }
 * )
 * console.log(eventTypes)
 * ```
 * @param getRequest Get the axios request to execute the query
 * @param options The options for the query.
 * @returns
 */
export function useSeamInfinityQuery<
  const TRequest extends SeamHttpRequest<any, any>
>(
  getRequest: (client: SeamHttp) => TRequest,
  options: UseSeamInfinityQueryOptions<TRequest>
): UseSeamInfinityQueryResult<TRequest> {
  const workspace_id = useCurrentWorkspaceId()
  assert(workspace_id, "workspace_id is required")

  const client = getSeamHttpClient(workspace_id)
  const request = getRequest(client)
  const queryKey =
    options?.queryKey ?? deriveQueryKeyFromRequest(workspace_id, request)
  const queryFn = () => {
    return request.execute()
  }

  return useInfiniteQuery({
    ...options,
    queryKey,
    queryFn,
  })
}

type PostRoutes = {
  [K in keyof Routes as "POST" extends Routes[K]["method"]
    ? K
    : never]: Routes[K]
}

/**
 * Use a seam mutation to mutate data on the server.
 * @example
 * ```tsx
 * const mutation = useSeamMutation("/acs/users/update")
 * mutation.mutate({
 *   acs_user_id: user.acs_user_id,
 *   access_schedule: {
 *     starts_at: "2022-01-01T00:00:00Z",
 *     ends_at: "2022-01-01T00:00:00Z",
 *   }
 * })
 * ```
 * @param path The path to the API endpoint
 */
export function useSeamMutation<
  TPath extends keyof PostRoutes,
  TVariables extends RouteRequestBody<TPath>,
  TError = DefaultError,
  TContext = unknown
>(
  path: TPath,
  options?: Omit<
    UseMutationOptions<RouteResponse<TPath>, TError, TVariables, TContext>,
    "mutationFn"
  >
): UseMutationResult<RouteResponse<TPath>, TError, TVariables, TContext> {
  const workspace_id = useCurrentWorkspaceId()
  assert(workspace_id, "workspace_id is required")
  const axios = getAuthenticatedAxios(workspace_id)
  const queryKey = [
    { workspace_id },
    ...path.split("/").filter(Boolean).slice(0, -1),
  ]
  const qc = useQueryClient()
  return useMutation({
    ...options,
    async onSuccess(data, variables, onMutateResult, context) {
      await qc.invalidateQueries({ queryKey })
      if (options?.onSuccess) {
        await options.onSuccess(data, variables, onMutateResult, context)
      }
    },
    mutationFn: (variables: TVariables) =>
      axios.post(path as any, variables as any).then((r) => r.data),
  })
}

/**
 * Similar to `useSeamMutation`, but invalidates the devices query on success.
 * @param path The path to the API endpoint
 */
export function useDeviceMutation<
  TPath extends keyof PostRoutes,
  TVariables extends RouteRequestBody<TPath>,
  TError = DefaultError,
  TContext = unknown
>(
  path: TPath,
  options?: Omit<
    UseMutationOptions<any, TError, TVariables, TContext>,
    "mutationFn"
  >
) {
  const workspace_id = useCurrentWorkspaceId()
  assert(workspace_id, "workspace_id is required")
  const qc = useQueryClient()

  return useSeamMutation(path, {
    ...options,
    onSuccess(...args) {
      qc.invalidateQueries({ queryKey: [{ workspace_id }, "devices"] })
      options?.onSuccess?.(...args)
    },
  })
}

/**
 * Use a seam poll action attempt mutation to poll an action attempt until it is successful.
 * @example
 * ```tsx
 * const mutation = useSeamPollActionAttemptMutation({
 *   onSuccess: (action_attempt) => {
 *     // handle success
 *   }
 * })
 * mutation.mutate({
 *  action_attempt_id: action_attempt.action_attempt_id,
 * })
 * ```
 */
export function useSeamPollActionAttemptMutation(
  options?: Omit<
    UseMutationOptions<
      RouteResponse<"/action_attempts/get">["action_attempt"],
      DefaultError,
      RouteRequestBody<"/action_attempts/get">,
      unknown
    >,
    "mutationFn"
  >
): UseMutationResult<
  RouteResponse<"/action_attempts/get">["action_attempt"],
  DefaultError,
  RouteRequestBody<"/action_attempts/get">,
  unknown
> {
  const path = "/action_attempts/get"
  const workspace_id = useCurrentWorkspaceId()
  assert(workspace_id, "workspace_id is required")
  const axios = getAuthenticatedAxios(workspace_id)
  return useMutation({
    retryDelay: 1_000,
    ...options,
    retry(failure_count, error) {
      if (error.message === "pending" && failure_count < 30) return true

      return false
    },
    mutationFn: async (variables: RouteRequestBody<"/action_attempts/get">) => {
      const r = await axios.post(path, variables)
      if (r.data.action_attempt) {
        if (r.data.action_attempt.status === "success") {
          return r.data.action_attempt as any
        }
        if (r.data.action_attempt.status === "error") {
          throw r.data.action_attempt.error
        }

        throw new Error("pending")
      }
      throw new Error("action_attempt not found")
    },
  })
}

export function useSeamPollActionAttemptQuery(
  variables: RouteRequestBody<"/action_attempts/get">,
  options: Omit<
    UseQueryOptions<
      RouteResponse<"/action_attempts/get">["action_attempt"],
      DefaultError,
      RouteResponse<"/action_attempts/get">["action_attempt"]
    >,
    "queryFn" | "queryKey"
  > & {
    onSuccess: (
      action_attempt: Extract<
        RouteResponse<"/action_attempts/get">["action_attempt"],
        { status: "success" }
      >
    ) => void
    onError: (error: DefaultError) => void
  }
) {
  const path = "/action_attempts/get"
  const workspace_id = useCurrentWorkspaceId()
  assert(workspace_id, "workspace_id is required")
  const axios = getAuthenticatedAxios(workspace_id)
  const queryKey = [
    { workspace_id },
    "action_attempts",
    "get",
    {
      action_attempt_id: variables.action_attempt_id,
    },
  ]
  const MAX_RETRIES = 30

  const reactQueryBag = useQuery({
    retryDelay: 1_000,
    queryKey,
    ...options,
    retry(failure_count, error) {
      if (error.message === "pending" && failure_count < MAX_RETRIES)
        return true

      return false
    },
    queryFn: async () => {
      const r = await axios.post(path, variables)
      if (r.data.action_attempt) {
        if (r.data.action_attempt.status === "success") {
          return r.data.action_attempt as any
        }
        if (r.data.action_attempt.status === "error") {
          throw r.data.action_attempt.error
        }
        throw new Error("pending")
      }
      throw new Error("action_attempt not found")
    },
  })

  const qc = useQueryClient()

  useEffect(() => {
    if (reactQueryBag.data) {
      if (reactQueryBag.data.status === "success") {
        options.onSuccess(reactQueryBag.data)
        return
      }
    }
    if (
      (reactQueryBag.error && reactQueryBag.failureCount >= MAX_RETRIES) ||
      (reactQueryBag.error && reactQueryBag.error.message !== "pending")
    ) {
      options.onError(reactQueryBag.error)
      return
    }
  }, [reactQueryBag.data, reactQueryBag.error])

  useEffect(() => {
    return () => {
      qc.invalidateQueries({ queryKey })
    }
  }, [])

  return reactQueryBag
}

/**
 * Use a seam query to fetch data from the server using a path-based approach.
 * Always uses POST requests for consistency.
 * @example
 * ```tsx
 * const { data: webhooks } = useSeamQueryV2("/webhooks/list")
 * console.log(webhooks)
 * ```
 * @param path The path to the API endpoint
 * @param options The options for the query.
 * @returns
 */
export function useSeamQueryV2<
  TPath extends keyof PostRoutes,
  TResponse = RouteResponse<TPath>
>(
  path: TPath,
  body: RouteRequestBody<TPath> = {},
  options?: Omit<UseQueryOptions<TResponse>, "queryFn" | "queryKey"> & {
    queryKey?: UseQueryOptions<TResponse>["queryKey"]
  }
): UseQueryResult<TResponse> {
  const workspace_id = useCurrentWorkspaceId()
  assert(workspace_id, "workspace_id is required")

  const axios = getAuthenticatedAxios(workspace_id)
  const queryKey = options?.queryKey ?? [
    { workspace_id },
    ...path.split("/").filter(Boolean),
    body,
  ]

  const queryFn = () => {
    return axios.post(path as any, body as any).then((r) => r.data)
  }

  return useQuery({ ...options, queryKey, queryFn })
}

/**
 * Use multiple seam queries to fetch data from the server in parallel.
 * Always uses POST requests for consistency.
 * @example
 * ```tsx
 * const { data, pending } = useSeamQueriesV2("/devices/list", [
 *   { device_type: "august_lock" },
 *   { device_type: "schlage_lock" }
 * ])
 *
 * // Access individual results
 * const augustDevices = data[0]
 * const schlageDevices = data[1]
 * ```
 * @param path The path to the API endpoint
 * @param bodies Array of request bodies to send to the endpoint
 * @param options Optional query options to apply to all queries
 * @returns Combined query results with data array and pending state
 */
export function useSeamQueriesV2<
  TPath extends keyof PostRoutes,
  TBodies extends Array<RouteRequestBody<TPath>>
>(
  path: TPath,
  bodies: TBodies,
  options?: Omit<
    UseQueryOptions<RouteResponse<TPath>>,
    "queryFn" | "queryKey"
  > & {
    queryKey?: UseQueryOptions<RouteResponse<TPath>>["queryKey"]
  }
): {
  data: RouteResponse<TPath>[]
  pending: boolean
  results?: UseQueryResult<RouteResponse<TPath>>[]
  isLoading: boolean
  isError: boolean
} {
  const workspace_id = useCurrentWorkspaceId()
  assert(workspace_id, "workspace_id is required")

  const axios = getAuthenticatedAxios(workspace_id)

  return useQueries({
    queries: bodies.map((body) => ({
      queryKey: options?.queryKey ?? [
        { workspace_id },
        ...path.split("/").filter(Boolean),
        body,
      ],
      queryFn: () => axios.post(path as any, body as any).then((r) => r.data),
      ...options,
    })),
    combine: (results) => {
      return {
        results,
        data: results.map((result) => result.data ?? {}).flat(),
        pending: results.some((result) => result.isPending),
        isLoading: results.some((result) => result.isLoading),
        isError: results.some((result) => result.isError),
        error: results.find((result) => result.isError)?.error,
      }
    },
  })
}
